import classnames from 'classnames';
import {
    ReactNode,
    useCallback,
    useId,
    useReducer,
    KeyboardEvent,
    useMemo,
    useEffect,
} from 'react';
import styles from './styles.module.scss';

// How long after the user stops typing do we consider them to have finished typing
const TYPING_TIMEOUT = 500;

type Action =
    | { type: 'ArrowDown pressed' }
    | { type: 'ArrowUp pressed' }
    | { type: 'Alt+ArrowUp pressed' }
    | { type: 'Space pressed' }
    | { type: 'Enter pressed' }
    | { type: 'Home pressed' }
    | { type: 'End pressed' }
    | { type: 'Escape pressed' }
    | { type: 'PageUp pressed' }
    | { type: 'PageDown pressed' }
    | { type: 'Tab pressed' }
    | { type: 'Printable character pressed'; payload: { character: string } }
    | { type: 'Combobox was clicked' }
    | { type: 'Combobox lost focus' }
    | { type: 'Option was clicked'; payload: { optionIndex: number } }
    | { type: 'Value changed'; payload: { optionIndex: number } };

type BaseSelectState<Type extends { [key: string]: any }> = {
    selectedOptionIndex: number;
    focussedOptionIndex: number;
    searchString: string;
    lastPrintibaleCharacterPressedAt: number;
    options: Array<Type>;
    labelKey: keyof Type;
};
type ClosedSelectState<Type extends { [key: string]: any }> = BaseSelectState<Type> & {
    isSelectOpen: false;
};
type OpenSelectState<Type extends { [key: string]: any }> = BaseSelectState<Type> & {
    isSelectOpen: true;
};
type SelectState<Type extends { [key: string]: any }> =
    | ClosedSelectState<Type>
    | OpenSelectState<Type>;

const closedSelectReducer = <Type extends { [key: string]: any }>(
    state: ClosedSelectState<Type>,
    action: Action
): SelectState<Type> => {
    switch (action.type) {
        case 'Space pressed':
        case 'Enter pressed':
        case 'ArrowDown pressed':
            return { ...state, isSelectOpen: true };
        case 'ArrowUp pressed':
            return { ...state, isSelectOpen: true, focussedOptionIndex: 0 };
        case 'Home pressed':
            return { ...state, isSelectOpen: true, focussedOptionIndex: 0 };
        case 'End pressed':
            return { ...state, isSelectOpen: true, focussedOptionIndex: state.options.length - 1 };
        case 'Combobox was clicked':
            return { ...state, isSelectOpen: true, focussedOptionIndex: state.selectedOptionIndex };
        case 'Printable character pressed':
            const optionMatchesSearch = (option: Type, searchString: string): boolean => {
                return (
                    option[state.labelKey].toLowerCase().indexOf(searchString.toLowerCase()) === 0
                );
            };
            const isNewSearch =
                Date.now() - state.lastPrintibaleCharacterPressedAt > TYPING_TIMEOUT;
            const hasTypedSameCharacter =
                state.searchString.length === 1 &&
                state.searchString[0] === action.payload.character;
            const newSearchString =
                isNewSearch === true || hasTypedSameCharacter === true
                    ? action.payload.character
                    : `${state.searchString}${action.payload.character}`;

            const nextFoundIndex = state.options.findIndex((option, index) => {
                return (
                    index > state.focussedOptionIndex &&
                    optionMatchesSearch(option, newSearchString)
                );
            });
            const previousFoundIndex = state.options.findIndex((option, index) => {
                return (
                    index <= state.focussedOptionIndex &&
                    optionMatchesSearch(option, newSearchString)
                );
            });

            const nextIndex =
                nextFoundIndex !== -1
                    ? nextFoundIndex
                    : previousFoundIndex !== -1
                      ? previousFoundIndex
                      : state.focussedOptionIndex;
            return {
                ...state,
                isSelectOpen: true,
                searchString: newSearchString,
                lastPrintibaleCharacterPressedAt: Date.now(),
                focussedOptionIndex: nextIndex,
            };
        case 'Value changed':
            return { ...state, selectedOptionIndex: action.payload.optionIndex };
    }
    return state;
};

const openSelectReducer = <Type extends { [key: string]: any }>(
    state: OpenSelectState<Type>,
    action: Action
): SelectState<Type> => {
    switch (action.type) {
        case 'ArrowDown pressed':
            return {
                ...state,
                focussedOptionIndex: Math.min(
                    state.focussedOptionIndex + 1,
                    state.options.length - 1
                ),
            };
        case 'ArrowUp pressed':
            return { ...state, focussedOptionIndex: Math.max(state.focussedOptionIndex - 1, 0) };
        case 'Space pressed':
        case 'Enter pressed':
        case 'Alt+ArrowUp pressed':
            return {
                ...state,
                isSelectOpen: false,
                selectedOptionIndex: state.focussedOptionIndex,
            };
        case 'Escape pressed':
            return { ...state, isSelectOpen: false };
        case 'Home pressed':
            return { ...state, focussedOptionIndex: 0 };
        case 'End pressed':
            return { ...state, focussedOptionIndex: state.options.length - 1 };
        case 'PageUp pressed':
            return { ...state, focussedOptionIndex: Math.max(state.focussedOptionIndex - 10, 0) };
        case 'PageDown pressed':
            return {
                ...state,
                focussedOptionIndex: Math.min(
                    state.focussedOptionIndex + 10,
                    state.options.length - 1
                ),
            };
        case 'Tab pressed':
            return {
                ...state,
                isSelectOpen: false,
                selectedOptionIndex: state.focussedOptionIndex,
            };
        case 'Combobox was clicked':
            return { ...state, isSelectOpen: false };
        case 'Option was clicked':
            return {
                ...state,
                isSelectOpen: false,
                selectedOptionIndex: action.payload.optionIndex,
            };
        case 'Printable character pressed':
            const optionMatchesSearch = (option: Type, searchString: string): boolean => {
                return (
                    option[state.labelKey].toLowerCase().indexOf(searchString.toLowerCase()) === 0
                );
            };
            const isNewSearch =
                Date.now() - state.lastPrintibaleCharacterPressedAt > TYPING_TIMEOUT;
            const hasTypedSameCharacter =
                state.searchString.length === 1 &&
                state.searchString[0] === action.payload.character;
            const newSearchString =
                isNewSearch === true || hasTypedSameCharacter === true
                    ? action.payload.character
                    : `${state.searchString}${action.payload.character}`;

            const nextFoundIndex = state.options.findIndex((option, index) => {
                return (
                    index > state.focussedOptionIndex &&
                    optionMatchesSearch(option, newSearchString)
                );
            });
            const previousFoundIndex = state.options.findIndex((option, index) => {
                return (
                    index <= state.focussedOptionIndex &&
                    optionMatchesSearch(option, newSearchString)
                );
            });

            const nextIndex =
                nextFoundIndex !== -1
                    ? nextFoundIndex
                    : previousFoundIndex !== -1
                      ? previousFoundIndex
                      : state.focussedOptionIndex;
            return {
                ...state,
                isSelectOpen: true,
                searchString: newSearchString,
                lastPrintibaleCharacterPressedAt: Date.now(),
                focussedOptionIndex: nextIndex,
            };
        case 'Combobox lost focus':
            return {
                ...state,
                isSelectOpen: false,
                selectedOptionIndex: state.focussedOptionIndex,
            };
        case 'Option was clicked':
            return {
                ...state,
                isSelectOpen: false,
                selectedOptionIndex: action.payload.optionIndex,
                focussedOptionIndex: action.payload.optionIndex,
            };
        case 'Value changed':
            return { ...state, selectedOptionIndex: action.payload.optionIndex };
    }
    return state;
};

const selectReducer = <Type extends { [key: string]: any }>(
    state: SelectState<Type>,
    action: Action
): SelectState<Type> => {
    if (state.isSelectOpen === true) {
        return openSelectReducer(state, action);
    } else {
        return closedSelectReducer(state, action);
    }
};

type SingleSelectProps<Type extends { [key: string]: any }, Key extends keyof Type = never> = {
    options: Array<Type>;
    value: Type;
    valueKey: keyof Type;
    labelKey: Type[Key] extends string ? Key : never;
    ['aria-label']?: string;
    ['aria-labelledby']?: string;
    ['aria-describedby']?: string;
    children?: (item: Type) => ReactNode;
    onChange: (item: Type) => any;
};
export const SingleSelect = <Type extends { [key: string]: any }, Key extends string>(
    props: SingleSelectProps<Type, Key>
) => {
    const { options, value, labelKey, valueKey, children, onChange } = props;
    const id = useId();
    const [state, dispatch] = useReducer(selectReducer, {
        isSelectOpen: false,
        selectedOptionIndex: options.findIndex((option) => option[valueKey] === value[valueKey]),
        focussedOptionIndex: 0,
        searchString: '',
        lastPrintibaleCharacterPressedAt: 0,
        options: options,
        labelKey: labelKey,
    });

    const handleComboboxKeyDown = useCallback(
        (event: KeyboardEvent) => {
            const { key, altKey, ctrlKey, metaKey } = event;
            switch (key) {
                case 'ArrowDown':
                case 'Enter':
                case 'Home':
                case 'End':
                case 'Escape':
                case 'PageUp':
                case 'PageDown':
                    event.preventDefault();
                    return dispatch({ type: `${key} pressed` });
                case ' ':
                    event.preventDefault();
                    return dispatch({ type: 'Space pressed' });
            }
            if (key === 'ArrowUp') {
                event.preventDefault();
                if (altKey) {
                    return dispatch({ type: 'Alt+ArrowUp pressed' });
                } else {
                    return dispatch({ type: 'ArrowUp pressed' });
                }
            }
            if (key === 'Tab') {
                return dispatch({ type: `Tab pressed` });
            }
            if (
                key === 'Backspace' ||
                key === 'Clear' ||
                (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
            ) {
                return dispatch({
                    type: 'Printable character pressed',
                    payload: { character: key },
                });
            }
        },
        [dispatch]
    );

    const handleBlur = useCallback(() => {
        dispatch({ type: 'Combobox lost focus' });
    }, [dispatch]);

    const handleComboboxClick = useCallback(() => {
        dispatch({ type: 'Combobox was clicked' });
    }, [dispatch]);

    const onOptionClick = useCallback(
        (index: number) => {
            dispatch({ type: 'Option was clicked', payload: { optionIndex: index } });
        },
        [dispatch]
    );

    const ItemRenderer = useMemo(() => {
        if (children) {
            return children;
        }
        return function DefaultItemRenderer(option: Type) {
            return <>{option[labelKey]}</>;
        };
    }, [children, labelKey]);

    const selectedOption = useMemo((): Type | undefined => {
        return options[state.selectedOptionIndex];
    }, [state.selectedOptionIndex, options]);

    useEffect(() => {
        if (selectedOption) {
            onChange(selectedOption);
        }
    }, [selectedOption, onChange]);

    useEffect(() => {
        const optionIndex = options.findIndex((option) => option[valueKey] === value[valueKey]);
        dispatch({ type: 'Value changed', payload: { optionIndex } });
    }, [valueKey, options, value, dispatch]);

    return (
        <section className={styles.comboboxContainer}>
            <div
                id={`${id}-combobox`}
                className={classnames(styles.combobox, { [styles.open]: state.isSelectOpen })}
                tabIndex={0}
                aria-controls={`${id}-listbox`}
                aria-expanded={state.isSelectOpen}
                aria-haspopup="listbox"
                aria-label={props['aria-label']}
                aria-labelledby={props['aria-labelledby']}
                aria-describedby={props['aria-describedby']}
                aria-activedescendant={`${id}-option-${state.selectedOptionIndex}`}
                role="combobox"
                onKeyDown={handleComboboxKeyDown}
                onBlur={handleBlur}
                onClick={handleComboboxClick}
            >
                <div className={styles.currentValue}>
                    {ItemRenderer(selectedOption || options[0])}
                </div>
            </div>
            <div
                id={`${id}-listbox`}
                className={classnames(styles.listbox, { [styles.open]: state.isSelectOpen })}
                role="listbox"
                tabIndex={-1}
            >
                {options.map((option, index) => {
                    return (
                        <div
                            id={`${id}-option-${index}`}
                            key={option[valueKey]}
                            className={classnames(styles.option, {
                                [styles.current]: state.focussedOptionIndex === index,
                            })}
                            aria-selected={state.selectedOptionIndex === index}
                            role="option"
                            onMouseDown={() => onOptionClick(index)}
                        >
                            {ItemRenderer(option)}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};
